import React, { useEffect, useRef } from 'react';
import { Button, LoadingButton } from '@one-thd/sui-atomic-components';
import { isMobile } from 'react-device-detect';
import { ItemToReturn } from '@src/models/returns';
import { useProcessValidator } from '@src/hooks/useProcessValidator';
import { useItemEditRemove } from '@src/hooks/useItemEditRemove';
import { useRefundCalculator } from '@src/hooks/useRefundCalculator';
import {
  printPackingSlipBrowser,
  printPackingSlipNative,
  PackingSlipData,
} from '@src/utils/packingSlip';
import { redirectToOrderDetails, getWindow } from '@src/utils/general';
import {
  emitReturnsFlowStep,
  emitReturnsOutcome,
} from '@src/utils/returns-instrumentation-context';
import { useReturnStore } from '../../stores/returnStore';
import { ReturnHeaders } from '../shared/ReturnHeaders';
import ReturnReviewCard from '../shared/summary-cards/ReturnReviewCard';
import { RefundTotal } from '../shared/RefundTotal';
import ReviewItemDetails from './ReviewItemDetails';
import FraudModal from '../shared/fraud/FraudModal';
import RemoveItemModal from './modal/RemoveItemModal';
import ErrorBanner from '../shared/ErrorBanner';

const ApoFpoFinalizeStep = () => {
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const orderBarcode = useReturnStore((state) => state.orderBarcode);
  const orderDetailsPath = useReturnStore((state) => state.orderDetailsPath);
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const returnEligibilityDetails = useReturnStore(
    (state) => state.returnEligibilityDetails,
  );

  // APO/FPO is client-side only — it never calls createReturn/Apriss, so this
  // terminal step is the only place the funnel can observe it. The refs dedupe
  // within a single mount (re-render, double-click); they reset if the step
  // unmounts and remounts, same as the guard in ReviewReturnDetailsStep.
  const hasEmittedReviewViewed = useRef(false);
  const hasPrinted = useRef(false);

  useEffect(() => {
    if (!hasEmittedReviewViewed.current) {
      hasEmittedReviewViewed.current = true;
      // New Relic: funnel — the APO/FPO finalize screen was reached.
      emitReturnsFlowStep('review_viewed');
    }
  }, []);

  const { calculateRefund } = useRefundCalculator({
    orderDetailsV2,
  });

  const recalculateRefund = (modifiedSelectedItems?: ItemToReturn[]) =>
    calculateRefund(modifiedSelectedItems || selectedItems);

  const {
    processPolicyValidator,
    renderModal,
    returnFraudType,
    policyValidatorAlert,
    setPolicyValidatorAlert,
    isPolicyValidatorLoading,
  } = useProcessValidator({
    orderDetailsV2,
    returnEligibilityDetails,
    isMilitaryExchangeGuest,
    proceedToNextStep: recalculateRefund,
  });

  const {
    onEditItemSubmit,
    onRemoveItemSubmit,
    onConfirmRemoveItem,
    onCancelRemoveItem,
    renderRemoveItemModal,
  } = useItemEditRemove({
    selectedItems,
    processPolicyValidator,
    setPolicyValidatorAlert,
    proceedToNextStep: recalculateRefund,
    orderDetailsPath,
    isMilitaryExchangeGuest,
  });

  const handlePrintPackingSlip = () => {
    const items = selectedItems.map((item) => ({
      name: item.brandName || '',
      description: item.itemDescription || '',
      quantity: item.returnQuantity,
      weight: item.itemWeight * item.returnQuantity,
      value: item.itemCost * item.returnQuantity,
    }));

    const data: PackingSlipData = {
      orderId: orderDetailsV2?.orderNumber || '',
      orderBarcode: orderBarcode || '',
      items,
    };

    const isNativeApp =
      getWindow()?.__EXPERIENCE_CONTEXT__?.isConsumerApp || false;
    if (isNativeApp) {
      printPackingSlipNative(data);
    } else {
      printPackingSlipBrowser(data);
    }

    if (!hasPrinted.current) {
      hasPrinted.current = true;
      emitReturnsOutcome('print_label', 'success');
      // New Relic: conversion — printing the packing slip is the terminal action
      // for APO/FPO, so emit return_created here to keep the funnel's "Created"
      // step honest, mirroring the client-side In-Store hand-off in
      // ReviewReturnDetailsStep. Counted per mount, not per session.
      emitReturnsFlowStep('return_created', { result: 'success' });
    }
  };

  const handleBackToOrderDetails = () => {
    // Leaving without ever printing means the customer has no packing slip and
    // the return cannot be completed — countable separately from a print.
    if (!hasPrinted.current) {
      emitReturnsOutcome('print_label', 'abandoned');
    }
    redirectToOrderDetails(orderDetailsPath, isMilitaryExchangeGuest);
  };

  return (
    <div className="sui-max-w-[555px]">
      <ReturnHeaders title="Thanks for being our customer" />
      {policyValidatorAlert && <ErrorBanner />}
      <ReturnReviewCard header="Military Post Office Drop-Off">
        {selectedItems.map((item, index) => (
          <ReviewItemDetails
            key={item.lineItemId}
            item={item}
            index={index}
            onEditItemSubmit={onEditItemSubmit}
            onRemoveItemSubmit={onRemoveItemSubmit}
            isPolicyValidatorLoading={isPolicyValidatorLoading}
          />
        ))}
      </ReturnReviewCard>
      <div className="sui-mt-8">
        <RefundTotal />
      </div>
      <div className="sui-mt-12 sui-flex sui-flex-col sui-justify-end sui-items-center sm:sui-mt-[60px] sm:sui-flex-row">
        <div className="sui-flex sui-flex-col sui-gap-4 sui-w-full sm:sui-w-auto sm:sui-flex-row-reverse">
          <LoadingButton
            data-testid="print-packing-slip-button"
            variant="primary"
            type="button"
            onClick={handlePrintPackingSlip}
            fullWidth={isMobile}
          >
            <span className="sui-whitespace-nowrap">Print Packing Slip</span>
          </LoadingButton>
          <Button
            data-testid="back-to-order-details-button"
            variant="secondary"
            type="button"
            onClick={handleBackToOrderDetails}
            fullWidth={isMobile}
          >
            Back to Order Details
          </Button>
        </div>
      </div>
      {renderModal && (
        <FraudModal
          returnFraudType={returnFraudType}
          title={renderModal.title}
          body={renderModal.bodyMessage}
          buttonText={renderModal.buttonMessage}
          onClick={renderModal.onClick}
        />
      )}
      {renderRemoveItemModal && (
        <RemoveItemModal
          onConfirmRemove={onConfirmRemoveItem}
          onCancelRemove={onCancelRemoveItem}
        />
      )}
    </div>
  );
};

export default ApoFpoFinalizeStep;
